import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout/MainLayout';
import apiClient from '../../api/client';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, Pencil, Trash2, Loader2, ImageOff, X, Upload, FileText, QrCode, ExternalLink } from 'lucide-react';

interface InstallationInstruction {
  id: number;
  partNumber: string;
  notes: string | null;
  qrUrl: string | null;
  guideUrl: string | null;
  yearStart: number | null;
  yearEnd: number | null;
  makes: string[];
  models: string[];
  vehicleCount: number;
}

interface InstallationStep {
  id: number;
  instructionId: number;
  stepNumber: number;
  description: string;
  imageFileName: string | null;
  createDate: string;
}

interface StepModalState {
  open: boolean;
  mode: 'add' | 'edit';
  step?: InstallationStep;
}

function StepImagePreview({ fileName, alt }: { fileName: string; alt: string }) {
  const token = localStorage.getItem('token');
  const src = `/api/v1/installation-instructions/step-images/${encodeURIComponent(fileName)}?token=${token}`;
  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover"
      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
    />
  );
}

export default function InstallationInstructionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [instruction, setInstruction] = useState<InstallationInstruction | null>(null);
  const [steps, setSteps] = useState<InstallationStep[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modal, setModal] = useState<StepModalState>({ open: false, mode: 'add' });

  // Modal form state
  const [formDescription, setFormDescription] = useState('');
  const [formImageFile, setFormImageFile] = useState<File | null>(null);
  const [formImagePreview, setFormImagePreview] = useState<string | null>(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingStepId, setDeletingStepId] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // QR code state
  const [qrUrlInput, setQrUrlInput] = useState('');
  const [qrPreview, setQrPreview] = useState<string | null>(null);
  const [isSavingQr, setIsSavingQr] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const [instrRes, stepsRes]: [any, any] = await Promise.all([
        apiClient.get(`/installation-instructions/${id}`),
        apiClient.get(`/installation-instructions/${id}/steps`),
      ]);
      const instr = instrRes.data || instrRes;
      setInstruction(instr);
      setQrUrlInput(instr.qrUrl || '');
      setSteps(stepsRes.data || stepsRes || []);
    } catch {
      toast.error('Failed to load instruction');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const openAddModal = () => {
    setFormDescription('');
    setFormImageFile(null);
    setFormImagePreview(null);
    setRemoveExistingImage(false);
    setModal({ open: true, mode: 'add' });
  };

  const openEditModal = (step: InstallationStep) => {
    setFormDescription(step.description);
    setFormImageFile(null);
    setFormImagePreview(null);
    setRemoveExistingImage(false);
    setModal({ open: true, mode: 'edit', step });
  };

  const closeModal = () => {
    if (formImagePreview) URL.revokeObjectURL(formImagePreview);
    setModal({ open: false, mode: 'add' });
    setFormImageFile(null);
    setFormImagePreview(null);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (formImagePreview) URL.revokeObjectURL(formImagePreview);
    setFormImageFile(file);
    setFormImagePreview(URL.createObjectURL(file));
    setRemoveExistingImage(false);
  };

  const handleSaveStep = async () => {
    if (!formDescription.trim()) {
      toast.error('Description is required');
      return;
    }
    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append('description', formDescription.trim());
      if (formImageFile) formData.append('image', formImageFile);
      if (modal.mode === 'edit' && removeExistingImage) {
        formData.append('removeImage', 'true');
      }

      if (modal.mode === 'add') {
        await apiClient.post(`/installation-instructions/${id}/steps`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Step added');
      } else {
        await apiClient.put(`/installation-instructions/steps/${modal.step!.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Step updated');
      }

      closeModal();
      await loadData();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save step');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteStep = async (step: InstallationStep) => {
    if (!window.confirm(`Delete Step ${step.stepNumber}?`)) return;
    setDeletingStepId(step.id);
    try {
      await apiClient.delete(`/installation-instructions/steps/${step.id}`);
      toast.success('Step deleted');
      setSteps(prev => {
        const remaining = prev.filter(s => s.id !== step.id);
        return remaining.map((s, i) => ({ ...s, stepNumber: i + 1 }));
      });
    } catch {
      toast.error('Failed to delete step');
    } finally {
      setDeletingStepId(null);
    }
  };

  const handleGenerateQr = async () => {
    if (!qrUrlInput.trim()) {
      toast.error('Enter a URL first');
      return;
    }
    setIsSavingQr(true);
    try {
      // Save URL to DB
      await apiClient.put(`/installation-instructions/${id}/qr-url`, { qrUrl: qrUrlInput.trim() });
      setInstruction(prev => prev ? { ...prev, qrUrl: qrUrlInput.trim() } : prev);
      // Fetch QR preview
      const res: any = await apiClient.get(`/installation-instructions/${id}/qr-preview`);
      setQrPreview((res.data || res).dataUrl);
      toast.success('QR code generated');
    } catch {
      toast.error('Failed to generate QR code');
    } finally {
      setIsSavingQr(false);
    }
  };

  const handleClearQr = async () => {
    setIsSavingQr(true);
    try {
      await apiClient.put(`/installation-instructions/${id}/qr-url`, { qrUrl: '' });
      setInstruction(prev => prev ? { ...prev, qrUrl: null } : prev);
      setQrUrlInput('');
      setQrPreview(null);
      toast.success('QR code removed');
    } catch {
      toast.error('Failed to remove QR code');
    } finally {
      setIsSavingQr(false);
    }
  };

  const handleGenerate = async () => {
    if (steps.length === 0) {
      toast.error('Add at least one step before generating');
      return;
    }
    setIsGenerating(true);
    try {
      const res: any = await apiClient.post(`/installation-instructions/${id}/generate`);
      const { url } = res.data || res;
      setInstruction(prev => prev ? { ...prev, guideUrl: url } : prev);
      toast.success('Guide generated — opening in new tab');
      const token = localStorage.getItem('token');
      window.open(`${url}?token=${token}`, '_blank');
    } catch {
      toast.error('Failed to generate guide');
    } finally {
      setIsGenerating(false);
    }
  };

  const formatYearRange = (start: number | null, end: number | null) => {
    if (!start && !end) return null;
    if (start === end) return `${start}`;
    return `${start} – ${end}`;
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-64 text-gray-500 dark:text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading...
        </div>
      </MainLayout>
    );
  }

  if (!instruction) {
    return (
      <MainLayout>
        <div className="p-6 text-gray-500 dark:text-gray-400">Instruction not found.</div>
      </MainLayout>
    );
  }

  const yearRange = formatYearRange(instruction.yearStart, instruction.yearEnd);
  const token = localStorage.getItem('token');

  return (
    <MainLayout>
      <div className="w-full">
        <div className="bg-white dark:bg-gray-800 shadow min-h-screen">

          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => navigate('/admin/installation-instructions')}
              className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-3 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Installation Instructions
            </button>

            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                    {instruction.partNumber}
                  </h1>
                  {instruction.guideUrl && (
                    <a
                      href={`${instruction.guideUrl}?token=${token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-full hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      View Guide
                    </a>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                  {yearRange && (
                    <span className="font-medium text-gray-700 dark:text-gray-300">{yearRange}</span>
                  )}
                  {instruction.makes.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {instruction.makes.map(make => (
                        <span key={make} className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                          {make}
                        </span>
                      ))}
                    </div>
                  )}
                  {instruction.models.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {instruction.models.map(model => (
                        <span key={model} className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          {model}
                        </span>
                      ))}
                    </div>
                  )}
                  {instruction.vehicleCount > 0 && (
                    <span className="text-xs text-gray-400">{instruction.vehicleCount} fitment{instruction.vehicleCount !== 1 ? 's' : ''}</span>
                  )}
                </div>
                {instruction.notes && (
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 italic">{instruction.notes}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || steps.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  {isGenerating ? 'Generating...' : 'Generate'}
                </button>
                <button
                  onClick={openAddModal}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  <Plus className="w-4 h-4" />
                  Add Step
                </button>
              </div>
            </div>
          </div>

          {/* QR Code section */}
          <div className="px-6 pb-2 pt-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <QrCode className="w-4 h-4" />
              QR Code
            </h2>
            <div className="flex items-start gap-3 max-w-2xl">
              <input
                type="url"
                value={qrUrlInput}
                onChange={(e) => setQrUrlInput(e.target.value)}
                placeholder="Enter a website or YouTube URL..."
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleGenerateQr}
                disabled={isSavingQr || !qrUrlInput.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                {isSavingQr ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
                {isSavingQr ? 'Generating...' : 'Generate QR Code'}
              </button>
              {instruction.qrUrl && (
                <button
                  onClick={handleClearQr}
                  disabled={isSavingQr}
                  className="px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Remove QR code"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* QR Preview */}
            {(qrPreview || instruction.qrUrl) && (
              <div className="mt-3 flex items-center gap-4 pb-4">
                {qrPreview ? (
                  <img src={qrPreview} alt="QR Code preview" className="w-24 h-24 border border-gray-200 dark:border-gray-700 rounded-lg p-1 bg-white" />
                ) : (
                  <div className="w-24 h-24 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center">
                    <QrCode className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                  </div>
                )}
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  <p className="font-medium text-gray-700 dark:text-gray-300 mb-0.5">Saved URL</p>
                  <p className="text-xs break-all max-w-md">{instruction.qrUrl}</p>
                  {!qrPreview && (
                    <button
                      onClick={handleGenerateQr}
                      className="mt-1.5 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      Preview QR code
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Steps */}
          <div className="p-6">
            {steps.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500">
                <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
                  <Plus className="w-7 h-7 opacity-40" />
                </div>
                <p className="text-sm font-medium">No steps yet</p>
                <p className="text-xs mt-1">Click "Add Step" to get started</p>
              </div>
            ) : (
              <div className="space-y-4 max-w-3xl">
                {steps.map(step => (
                  <div
                    key={step.id}
                    className="flex gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                  >
                    {/* Step number badge */}
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                      {step.stepNumber}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
                        {step.description}
                      </p>

                      {/* Image */}
                      {step.imageFileName && (
                        <div className="mt-3">
                          <div className="relative inline-block rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900" style={{ maxWidth: 320, maxHeight: 240 }}>
                            <div className="w-80 h-52">
                              <StepImagePreview fileName={step.imageFileName} alt={`Step ${step.stepNumber}`} />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex-shrink-0 flex items-start gap-1">
                      <button
                        onClick={() => openEditModal(step)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                        title="Edit step"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteStep(step)}
                        disabled={deletingStepId === step.id}
                        className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-40"
                        title="Delete step"
                      >
                        {deletingStepId === step.id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Trash2 className="w-4 h-4" />
                        }
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add / Edit Step Modal */}
      {modal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">

            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {modal.mode === 'add'
                  ? `Add Step ${steps.length + 1}`
                  : `Edit Step ${modal.step?.stepNumber}`
                }
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Describe this installation step..."
                  rows={4}
                  autoFocus
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Photo <span className="text-gray-400 font-normal">(optional)</span>
                </label>

                {/* Show existing image when editing (and not replacing/removing) */}
                {modal.mode === 'edit' && modal.step?.imageFileName && !formImageFile && !removeExistingImage && (
                  <div className="mb-3 relative inline-block">
                    <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 w-64 h-44">
                      <StepImagePreview fileName={modal.step.imageFileName} alt="Current image" />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Current image</p>
                    <button
                      onClick={() => setRemoveExistingImage(true)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors"
                      title="Remove image"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {/* New image preview */}
                {formImagePreview && (
                  <div className="mb-3 relative inline-block">
                    <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 w-64 h-44">
                      <img src={formImagePreview} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formImageFile?.name}</p>
                    <button
                      onClick={() => {
                        URL.revokeObjectURL(formImagePreview);
                        setFormImageFile(null);
                        setFormImagePreview(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {/* Removed message */}
                {removeExistingImage && !formImageFile && (
                  <div className="mb-3 flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
                    <ImageOff className="w-4 h-4" />
                    Image will be removed on save
                    <button
                      onClick={() => setRemoveExistingImage(false)}
                      className="text-xs underline ml-1"
                    >
                      Undo
                    </button>
                  </div>
                )}

                {/* Upload button */}
                {!formImagePreview && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-500 dark:hover:border-blue-500 dark:hover:text-blue-400 transition-colors w-full justify-center"
                  >
                    <Upload className="w-4 h-4" />
                    {modal.mode === 'edit' && modal.step?.imageFileName && !removeExistingImage
                      ? 'Replace image'
                      : 'Upload image'
                    }
                  </button>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageSelect}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 flex-shrink-0">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveStep}
                disabled={isSaving || !formDescription.trim()}
                className="flex items-center gap-2 px-5 py-2 text-sm bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSaving ? 'Saving...' : modal.mode === 'add' ? 'Add Step' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
